import React, { useState, useRef } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  Loader2, Upload, FileText, Check, ChevronRight, ChevronLeft, AlertCircle, Trophy,
} from 'lucide-react';
import { toast } from 'sonner';

const STEPS = ['Resume', 'Review & consent'];

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch { return iso; }
};

const fmtSize = (b) => (b >= 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.round(b / 1024)} KB`);

export function ApplyDialog({ open, onOpenChange, program, opportunities, children, application,
                             childId, priority1, priority2, onDone }) {
  const editing = application?.status === 'submitted';
  const [step, setStep] = useState(0);
  const [resume, setResume] = useState(application?.resume || null);
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef(null);

  const reset = () => { setStep(0); setConsent(false); setError(''); };
  const child = children.find(c => c.child_id === childId);
  const childName = child?.child_name || '';
  const oppById = (id) => opportunities.find(o => o.id === id);

  const uploadResume = async (file) => {
    if (!file) return;
    setBusy(true); setError('');
    const form = new FormData();
    form.append('child_id', childId);
    form.append('program_id', program.id);
    form.append('file', file);
    try {
      const r = await api.post('/parent/internship-applications/resume', form,
        { headers: { 'Content-Type': 'multipart/form-data' } });
      setResume({ filename: r.data.filename, size: r.data.size, uploaded_at: r.data.uploaded_at });
      toast.success('Resume uploaded');
    } catch (e) {
      setError(e?.response?.data?.detail || 'The upload failed. Please try again.');
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const submit = async () => {
    setBusy(true); setError('');
    try {
      await api.post('/parent/internship-applications', {
        child_id: childId,
        program_id: program.id,
        priority1_opportunity_id: priority1,
        priority2_opportunity_id: priority2 || null,
        consent: true,
      });
      toast.success(editing ? 'Application updated' : 'Application submitted');
      onDone?.();
      onOpenChange(false);
      reset();
    } catch (e) {
      setError(e?.response?.data?.detail || 'The application could not be submitted.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { onOpenChange(false); reset(); } }}>
      <DialogContent className="sm:max-w-xl rounded-2xl max-h-[88vh] overflow-y-auto" data-testid="apply-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Outfit'] text-lg pr-6">
            {editing ? 'Update application' : `Complete ${childName.split(' ')[0]}'s application`}
          </DialogTitle>
        </DialogHeader>

        <div className="flex items-center gap-2 -mt-1" data-testid="apply-steps">
          {STEPS.map((label, i) => (
            <div key={label} className="flex items-center gap-2 min-w-0">
              <span
                className={`h-5 w-5 rounded-full text-[10px] font-bold flex items-center justify-center shrink-0 ${
                  i < step ? 'bg-emerald-500 text-white' : i === step ? 'bg-[#F97D6A] text-white' : 'bg-gray-100 text-gray-400'}`}
              >
                {i < step ? <Check className="h-3 w-3" /> : i + 1}
              </span>
              <span className={`text-xs truncate ${i === step ? 'text-[#1F2937] font-semibold' : 'text-[#9CA3AF]'}`}>{label}</span>
              {i < STEPS.length - 1 && <ChevronRight className="h-3 w-3 text-gray-300 shrink-0" />}
            </div>
          ))}
        </div>

        {/* Step 1 — resume */}
        {step === 0 && (
          <div className="space-y-3" data-testid="apply-step-resume">
            <p className="text-sm text-[#6B7280]">
              Upload one resume for {childName}. The same file is shared with both preferences, and you can replace it
              until the deadline.
            </p>
            <input
              ref={fileRef}
              type="file"
              accept=".pdf,.doc,.docx"
              className="hidden"
              onChange={(e) => uploadResume(e.target.files?.[0])}
              data-testid="resume-file-input"
            />
            {resume ? (
              <div className="rounded-xl border border-[#E5E7EB] p-4 flex items-center justify-between gap-3" data-testid="resume-summary">
                <div className="flex items-center gap-3 min-w-0">
                  <FileText className="h-5 w-5 text-[#F97D6A] shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-[#1F2937] truncate">{resume.filename}</p>
                    <p className="text-xs text-[#9CA3AF]">{fmtSize(resume.size)} · uploaded {fmtDate(resume.uploaded_at)}</p>
                  </div>
                </div>
                <Button variant="outline" className="h-8 rounded-lg text-xs border-[#E5E7EB] shrink-0"
                        disabled={busy} onClick={() => fileRef.current?.click()} data-testid="resume-replace-btn">
                  {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Replace'}
                </Button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={busy}
                className="w-full rounded-xl border border-dashed border-[#E5E7EB] p-8 text-center hover:border-[#F97D6A] transition-colors"
                data-testid="resume-upload-btn"
              >
                {busy ? <Loader2 className="h-5 w-5 animate-spin text-[#F97D6A] mx-auto" /> : (
                  <>
                    <Upload className="h-5 w-5 text-[#9CA3AF] mx-auto mb-2" />
                    <p className="text-sm font-medium text-[#1F2937]">Choose a resume</p>
                    <p className="text-xs text-[#9CA3AF] mt-0.5">PDF, DOC or DOCX · up to 5 MB</p>
                  </>
                )}
              </button>
            )}
          </div>
        )}

        {/* Step 2 — review and consent */}
        {step === 1 && (
          <div className="space-y-3" data-testid="apply-step-review">
            <div className="rounded-xl bg-[#F8F9FA] p-4 space-y-2.5">
              <Row label="Student" value={`${childName} · Grade ${child?.grade || ''}`} testId="review-child" />
              <Row label="Preference 1" value={`${oppById(priority1)?.title} · ${oppById(priority1)?.company_name}`} testId="review-priority1" />
              <Row label="Preference 2" value={priority2 ? `${oppById(priority2)?.title} · ${oppById(priority2)?.company_name}` : 'Not chosen'} testId="review-priority2" />
              <Row label="Resume" value={resume?.filename || '—'} testId="review-resume" />
              <Row label="Programme" value={program.name} testId="review-program" />
            </div>

            <label className="flex items-start gap-3 rounded-xl border border-[#E5E7EB] p-4 cursor-pointer">
              <Checkbox checked={consent} onCheckedChange={(v) => setConsent(!!v)} className="mt-0.5" data-testid="apply-consent-checkbox" />
              <span className="text-sm text-[#4B5563] leading-relaxed">
                I consent to {childName}'s participation in this internship programme, and to this application and
                resume being shared with the school and the internship provider for the purpose of placement.
              </span>
            </label>
          </div>
        )}

        {error && <p className="text-xs text-red-600" data-testid="apply-error">{error}</p>}

        <div className="flex items-center justify-between gap-2 pt-1">
          <Button
            variant="outline"
            className="rounded-lg text-sm border-[#E5E7EB]"
            onClick={() => (step === 0 ? onOpenChange(false) : setStep(step - 1))}
            data-testid="apply-back-btn"
          >
            {step === 0 ? 'Cancel' : <><ChevronLeft className="h-4 w-4 mr-1" /> Back</>}
          </Button>
          {step === 0 ? (
            <Button
              className="rounded-lg text-sm bg-[#F97D6A] hover:bg-[#F06651] text-white"
              disabled={!resume}
              onClick={() => setStep(1)}
              data-testid="apply-next-btn"
            >
              Continue <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          ) : (
            <Button
              className="rounded-lg text-sm bg-[#F97D6A] hover:bg-[#F06651] text-white"
              disabled={!consent || busy}
              onClick={submit}
              data-testid="apply-submit-btn"
            >
              {busy ? <><Loader2 className="h-4 w-4 animate-spin mr-1.5" /> Submitting</> : editing ? 'Update application' : 'Submit application'}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function ApplicationCard({ application, onEdit }) {
  const a = application;
  return (
    <div className="rounded-2xl border border-[#E5E7EB] bg-white p-5" data-testid={`application-card-${a.child_id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-base font-bold text-[#1F2937] font-['Outfit']">{a.child_name}</p>
          <p className="text-xs text-[#9CA3AF] mt-0.5">Grade {a.child_grade} · {a.program_name}</p>
        </div>
        <Badge
          className={`border-0 text-[10px] ${
            a.final_status === 'selected' ? 'bg-emerald-500 text-white'
              : a.final_status === 'not_selected' ? 'bg-gray-100 text-gray-500'
              : a.status === 'submitted' ? (a.editable ? 'bg-emerald-50 text-emerald-600' : 'bg-sky-50 text-sky-600')
              : 'bg-amber-50 text-amber-600'}`}
          data-testid={`application-status-${a.child_id}`}
        >
          {a.parent_status || (a.status === 'submitted' ? 'Application Submitted' : 'Draft')}
        </Badge>
      </div>

      {a.final_status === 'selected' && a.final_placement && (
        <div className="mt-4 rounded-xl bg-[#F1FBF5] border border-emerald-100 px-4 py-3" data-testid={`application-placement-${a.child_id}`}>
          <p className="text-[11px] font-semibold text-emerald-700 uppercase tracking-wider">Confirmed internship</p>
          <p className="text-sm font-semibold text-[#1F2937] mt-1">{a.final_placement.title}</p>
          <p className="text-sm text-[#4B5563] mt-0.5">{a.final_placement.company_name}</p>
          <p className="text-sm text-[#6B7280] mt-1.5">
            {fmtDate(a.final_placement.start_date)} – {fmtDate(a.final_placement.end_date)}
            {a.final_placement.duration_days ? ` · ${a.final_placement.duration_days} day${a.final_placement.duration_days === 1 ? '' : 's'}` : ''}
            {a.final_placement.workplace_address ? ` · ${a.final_placement.workplace_address}` : ''}
          </p>
          <p className="text-[11px] text-[#6B7280] mt-1.5">The school will share any further logistics closer to the start date.</p>        </div>
      )}
      {a.parent_status === 'Under Review' && (
        <p className="mt-4 text-sm text-[#6B7280] leading-relaxed" data-testid={`application-under-review-${a.child_id}`}>
          Applications are closed and the school is working through the placements. You will be emailed as soon as
          {' '}{a.child_name}'s internship is confirmed.
        </p>
      )}
      {a.final_status === 'not_selected' && (
        <p className="mt-4 text-sm text-[#6B7280] leading-relaxed" data-testid={`application-not-selected-${a.child_id}`}>
          Places were limited this round, so we could not confirm a placement for {a.child_name} this time.
          More internship rounds and career experiences will follow.
        </p>
      )}

      <div className="mt-4 space-y-2">
        <Preference rank={1} opp={a.priority1} />
        {a.priority2
          ? <Preference rank={2} opp={a.priority2} />
          : <p className="text-xs text-[#9CA3AF] pl-7">No second preference</p>}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 mt-4 pt-3 border-t border-[#F3F4F6]">
        {a.resume ? (
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/parent/internship-applications/${a.id}/resume`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sm text-[#6B7280] hover:text-[#F97D6A] min-w-0"
            data-testid={`application-resume-${a.child_id}`}
          >
            <FileText className="h-3.5 w-3.5 shrink-0" /><span className="truncate">{a.resume.filename}</span>
          </a>
        ) : <span className="text-xs text-amber-600">Resume missing</span>}
        {a.editable ? (
          <Button variant="outline" className="h-8 rounded-lg text-xs border-[#E5E7EB]" onClick={() => onEdit(a)} data-testid={`application-edit-${a.child_id}`}>
            View / edit
          </Button>
        ) : (
          <span className="text-xs text-[#9CA3AF]">Applications closed {fmtDate(a.application_deadline)}</span>
        )}
      </div>

      {a.consent_given_at && (
        <p className="text-[10px] text-[#6B7280] mt-2" data-testid={`application-consent-${a.child_id}`}>
          Consent recorded {fmtDate(a.consent_given_at)}{a.revision_count > 0 ? ` · edited ${a.revision_count} time${a.revision_count === 1 ? '' : 's'}` : ''}
        </p>
      )}
    </div>
  );
}

const Preference = ({ rank, opp }) => (
  <div className="flex items-start gap-2.5">
    <span className="h-5 w-5 rounded-full bg-[#FFF0ED] text-[#F97D6A] text-[10px] font-bold flex items-center justify-center shrink-0 mt-0.5">
      {rank}
    </span>
    <div className="min-w-0">
      <p className="text-sm font-medium text-[#1F2937] truncate">{opp?.title || 'Not available'}</p>
      <p className="text-xs text-[#9CA3AF]">{opp?.company_name}{opp?.start_date ? ` · ${fmtDate(opp.start_date)}` : ''}</p>
    </div>
  </div>
);

const Row = ({ label, value, testId }) => (
  <div className="flex items-start justify-between gap-3" data-testid={testId}>
    <span className="text-xs text-[#9CA3AF] shrink-0">{label}</span>
    <span className="text-xs font-medium text-[#1F2937] text-right">{value}</span>
  </div>
);

export const ApplicationsSummary = ({ children, applications }) => {
  const applied = applications.filter(a => a.status === 'submitted').length;
  if (!children.length) return null;
  return (
    <p className="text-xs text-[#9CA3AF] flex items-center gap-1.5" data-testid="applications-summary">
      <Trophy className="h-3.5 w-3.5" />
      {applied} of {children.length} {children.length === 1 ? 'child has' : 'children have'} applied
    </p>
  );
};

export const ClosedNote = () => (
  <p className="text-xs text-[#9CA3AF] flex items-center gap-1.5" data-testid="closed-note">
    <AlertCircle className="h-3.5 w-3.5" /> Applications for this programme are closed.
  </p>
);
